import React, { useEffect, useMemo } from "react";
import { Link, useNavigate } from "react-router";

import {
  CaretLeftIcon,
  CaretRightIcon,
  ChevronRightIcon,
} from "@radix-ui/react-icons";

import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "~/components/ui/popover";
import { Separator } from "~/components/ui/separator";
import { P } from "~/components/ui/text";
import { useURL } from "~/contexts";
import type { C_ProductInfo } from "~/lib/app.types";
import type { SerializeFrom } from "~/lib/types";

import type { ProductDetailsHystoryState } from "../location-state/product-details.location-state";

export const ProductQuickNav = ({
  product,
  state,
}: {
  product: SerializeFrom<C_ProductInfo>;
  state: ProductDetailsHystoryState;
}) => {
  const url = useURL();
  const navigate = useNavigate();
  const [open, setOpen] = React.useState(false);

  useEffect(() => {
    window.addEventListener("keypress", keypressHandler, false);
    return function cleanup() {
      window.removeEventListener("keypress", keypressHandler);
    };
  });

  const keypressHandler = (event: KeyboardEvent) => {
    if (event.code === "KeyJ") {
      navigate(url(`/${prev?.id}.html`), {
        state: { ...state, product: prev, direction: "back" },
      });
    }
    if (event.code === "KeyK") {
      navigate(url(`/${next?.id}.html`), {
        state: { ...state, product: next, direction: "forward" },
      });
    }
  };

  const [next, prev] = useMemo(() => {
    const currentProductId = (product as C_ProductInfo)?.id;
    const index = state?.quickNav?.findIndex(p => p?.id === currentProductId);
    if (index === undefined || index === -1) return [undefined, undefined];
    return [
      state?.quickNav?.[index + 1] as SerializeFrom<C_ProductInfo>,
      state?.quickNav?.[index - 1] as SerializeFrom<C_ProductInfo>,
    ];
  }, [product, state?.quickNav]);

  const hasLoadMore =
    state.totalProducts &&
    state.quickNav &&
    state.totalProducts > state.quickNav.length;

  return (
    <>
      <div
        className="relative mx-auto flex w-full items-end text-sm font-normal text-neutral-11"
        id="quick-nav"
      >
        <div className="flex flex-grow-0 flex-row">
          {state?.quickNav && (
            <Link
              to={""}
              onClick={() => {
                navigate(-1);
              }}
              className="flex flex-row items-end"
            >
              <CaretLeftIcon className="flex h-[20px] w-[20px] pb-0" />
              <span className="flex">Back</span>
            </Link>
          )}
        </div>
        <div className="flex flex-grow-0 flex-row px-1 opacity-50">
          {prev && (
            <Link
              to={url(`/${prev?.id}.html`)}
              state={{ ...state, product: prev, direction: "back" }}
              replace
              preventScrollReset
              className="flex flex-row items-end"
            >
              <CaretLeftIcon className="flex h-4 w-4" />
              <span className="flex">Previous</span>
            </Link>
          )}

          {prev && (
            <Separator
              orientation="vertical"
              className="mx-2 my-1 flex h-auto bg-neutral-11 opacity-50 lg:-top-9"
            />
          )}

          {next && (
            <Link
              to={url(`/${next?.id}.html`)}
              state={{ ...state, product: next, direction: "forward" }}
              replace
              preventScrollReset
              className="flex flex-row items-end"
            >
              <span className="flex">Next</span>
              <CaretRightIcon className="flex h-4 w-4" />
            </Link>
          )}

          {!next && hasLoadMore && (
            <Popover open={open} onOpenChange={setOpen}>
              <PopoverTrigger className="text-gray-400">
                <span>Next</span>
                <ChevronRightIcon className="inline h-4 w-4" />
              </PopoverTrigger>
              <PopoverContent className="w-40 p-2 lg:-left-10" align="center">
                <Link
                  to={""}
                  onClick={() => {
                    navigate(-1);
                  }}
                >
                  <P>Back to product list</P>
                </Link>
              </PopoverContent>
            </Popover>
          )}
        </div>
      </div>
    </>
  );
};
